import type { BillingInterval, PaymentMethod, Plan, ScheduledChangeKind } from "../../../shared/types.js";
import {
  isScheduledChangePendingApproval as isScheduledChangePendingApprovalShared,
} from "../../../shared/scheduled_change.js";
import { formatBillingDate } from "./formatBillingDate.js";

export interface ScheduledChangeRow {
  kind: string;
  effectiveAt: string;
  payload?: Record<string, unknown>;
  provider_swap_status?: string | null;
  pending_order_id?: string | null;
}

export function isScheduledChangePendingApproval(change: ScheduledChangeRow): boolean {
  return isScheduledChangePendingApprovalShared({
    providerSwapStatus: change.provider_swap_status,
    pendingOrderId: change.pending_order_id,
  });
}

type TranslateFn = (key: string, opts?: Record<string, string>) => string;

const PLAN_RANK: Record<Plan, number> = { basic: 1, premium: 2 };
const INTERVAL_RANK: Record<BillingInterval, number> = { monthly: 1, annual: 2 };

function asPlan(value: unknown): Plan | undefined {
  return value === "basic" || value === "premium" ? value : undefined;
}

function asInterval(value: unknown): BillingInterval | undefined {
  return value === "monthly" || value === "annual" ? value : undefined;
}

function asPaymentMethod(value: unknown): PaymentMethod | undefined {
  return value === "stripe" || value === "openpix" ? value : undefined;
}

function planLabel(t: TranslateFn, plan: Plan | undefined): string {
  if (!plan) return "";
  return t(`plans.${plan}`);
}

function intervalLabel(t: TranslateFn, interval: BillingInterval | undefined): string {
  if (!interval) return "";
  return t(`plans.${interval}`);
}

function paymentMethodLabel(t: TranslateFn, method: PaymentMethod | undefined): string {
  if (method === "stripe") return t("checkout.cardLong");
  if (method === "openpix") return t("checkout.pixLong");
  return "";
}

/** Returns the open scheduled change that matches the user's current selection, if any. */
export function findMatchingScheduledChange(
  scheduledChanges: ScheduledChangeRow[],
  target: { plan: Plan; interval: BillingInterval; paymentMethod: PaymentMethod },
): ScheduledChangeRow | null {
  return (
    scheduledChanges.find((change) => {
      const payload = change.payload ?? {};
      return (
        payload.target_plan === target.plan &&
        payload.target_interval === target.interval &&
        payload.target_payment_method === target.paymentMethod
      );
    }) ?? null
  );
}

/** Human-readable description of a scheduled change that already took effect. */
export function describeAppliedScheduledChange(
  change: ScheduledChangeRow,
  t: TranslateFn,
  locale: string,
): string {
  const date = formatBillingDate(change.effectiveAt, locale);
  const payload = change.payload ?? {};
  const kind = change.kind as ScheduledChangeKind;

  const targetPlan = asPlan(payload.target_plan);
  const targetInterval = asInterval(payload.target_interval);
  const targetMethod = asPaymentMethod(payload.target_payment_method);

  switch (kind) {
    case "downgrade_plan":
      return t("integrated.scheduledChangeAppliedDowngradePlan", {
        plan: planLabel(t, targetPlan),
        interval: intervalLabel(t, targetInterval),
        date,
      });
    case "downgrade_interval":
      return t("integrated.scheduledChangeAppliedDowngradeInterval", {
        plan: planLabel(t, targetPlan),
        interval: intervalLabel(t, targetInterval),
        date,
      });
    case "payment_method_change":
      return t("integrated.scheduledChangeAppliedPaymentMethod", {
        method: paymentMethodLabel(t, targetMethod),
        date,
      });
    case "cancellation":
      return t("integrated.scheduledChangeAppliedCancellation", { date });
    default:
      return t("integrated.scheduledChangeAppliedGeneric", { date });
  }
}

/** True when the subscription already reflects a scheduled change that was applied. */
export function localSubscriptionMatchesAppliedChange(
  change: ScheduledChangeRow,
  local: {
    plan: Plan;
    interval: BillingInterval;
    paymentMethod: PaymentMethod;
    status: string;
  } | null,
): boolean {
  if (!local) return false;
  const payload = change.payload ?? {};
  const kind = change.kind as ScheduledChangeKind;
  if (kind === "cancellation") return local.status === "expired";
  if (kind === "payment_method_change") {
    return payload.target_payment_method === local.paymentMethod;
  }
  if (kind === "downgrade_plan" || kind === "downgrade_interval") {
    return payload.target_plan === local.plan && payload.target_interval === local.interval;
  }
  return false;
}

/** Human-readable description of a pending scheduled billing change. */
export function describeScheduledChange(
  change: ScheduledChangeRow,
  t: TranslateFn,
  locale: string,
): string {
  const date = formatBillingDate(change.effectiveAt, locale);
  const payload = change.payload ?? {};
  const kind = change.kind as ScheduledChangeKind;

  const targetPlan = asPlan(payload.target_plan);
  const targetInterval = asInterval(payload.target_interval);
  const targetMethod = asPaymentMethod(payload.target_payment_method);

  switch (kind) {
    case "downgrade_plan":
      return t("integrated.scheduledChangeDowngradePlan", {
        plan: planLabel(t, targetPlan),
        interval: intervalLabel(t, targetInterval),
        date,
      });
    case "downgrade_interval":
      return t("integrated.scheduledChangeDowngradeInterval", {
        plan: planLabel(t, targetPlan),
        interval: intervalLabel(t, targetInterval),
        date,
      });
    case "payment_method_change":
      return t("integrated.scheduledChangePaymentMethod", {
        method: paymentMethodLabel(t, targetMethod),
        date,
      });
    case "cancellation":
      return t("integrated.scheduledChangeCancellation", { date });
    default:
      return t("integrated.scheduledChangeGeneric", { date });
  }
}

export interface ScheduledChangeSupportCopy {
  title: string;
  body: string;
}

/** Infers the scheduled-change kind for a target selection (null if it would not be scheduled). */
export function inferScheduledChangeKind(
  current: { plan: Plan; interval: BillingInterval; paymentMethod: PaymentMethod },
  target: { plan: Plan; interval: BillingInterval; paymentMethod: PaymentMethod },
): ScheduledChangeKind | null {
  if (target.plan !== current.plan) {
    if (PLAN_RANK[target.plan] < PLAN_RANK[current.plan]) return "downgrade_plan";
    return null;
  }
  if (target.interval !== current.interval) {
    if (INTERVAL_RANK[target.interval] < INTERVAL_RANK[current.interval]) return "downgrade_interval";
    return null;
  }
  if (target.paymentMethod !== current.paymentMethod) return "payment_method_change";
  return null;
}

/** Human-readable description of a schedule the user is about to request. */
export function describeRequestedScheduleChange(
  args: {
    current: { plan: Plan; interval: BillingInterval; paymentMethod: PaymentMethod; currentPeriodEnd?: string };
    target: { plan: Plan; interval: BillingInterval; paymentMethod: PaymentMethod };
  },
  t: TranslateFn,
  locale: string,
): string {
  const kind = inferScheduledChangeKind(args.current, args.target);
  if (!kind) return "";
  const effectiveAt = args.current.currentPeriodEnd ?? new Date().toISOString();
  return describeScheduledChange(
    {
      kind,
      effectiveAt,
      payload: {
        target_plan: args.target.plan,
        target_interval: args.target.interval,
        target_payment_method: args.target.paymentMethod,
      },
    },
    t,
    locale,
  );
}

/** Human-readable description of the change the user is about to request (schedule or upgrade). */
export function describeRequestedChange(
  args: {
    current: { plan: Plan; interval: BillingInterval; paymentMethod: PaymentMethod; currentPeriodEnd?: string };
    target: { plan: Plan; interval: BillingInterval; paymentMethod: PaymentMethod };
    chargeNowCents?: number;
    dueTodayLabel?: string;
  },
  t: TranslateFn,
  locale: string,
): string {
  const scheduled = describeRequestedScheduleChange(args, t, locale);
  if (scheduled) return scheduled;

  const wouldUpgrade =
    PLAN_RANK[args.target.plan] > PLAN_RANK[args.current.plan] ||
    (args.target.plan === args.current.plan &&
      INTERVAL_RANK[args.target.interval] > INTERVAL_RANK[args.current.interval]);
  if (wouldUpgrade) {
    return t("integrated.replaceScheduledNewUpgrade", {
      plan: planLabel(t, args.target.plan),
      interval: intervalLabel(t, args.target.interval),
      amount: args.dueTodayLabel ?? "",
    });
  }

  return t("integrated.replaceScheduledNewGeneric", {
    plan: planLabel(t, args.target.plan),
    interval: intervalLabel(t, args.target.interval),
  });
}

/** Label for the button that reverses a scheduled change (shown in the scheduled-changes list). */
export function getCancelScheduledChangeActionLabel(
  change: ScheduledChangeRow,
  current: { plan: Plan; interval: BillingInterval; paymentMethod: PaymentMethod } | null,
  t: TranslateFn,
): string {
  const kind = change.kind as ScheduledChangeKind;
  if (kind === "downgrade_plan" && current?.plan === "premium") {
    return t("integrated.keepPremium");
  }
  return t("integrated.cancelScheduledChange");
}

/** Copy for the support dialog when canceling or overriding a scheduled change. */
export function getScheduledChangeSupportCopy(
  change: ScheduledChangeRow,
  current: { plan: Plan; interval: BillingInterval; paymentMethod: PaymentMethod } | null,
  reason: "cancel" | "replace" | "reverse_openpix_cancellation",
  t: TranslateFn,
  locale = "pt-BR",
): ScheduledChangeSupportCopy {
  const kind = change.kind as ScheduledChangeKind;

  if (reason === "replace") {
    return {
      title: t("integrated.scheduledChangeSupportTitleReplace"),
      body: t("integrated.scheduledChangeSupportBodyReplace"),
    };
  }

  if (reason === "reverse_openpix_cancellation" || (kind === "cancellation" && current?.paymentMethod === "openpix")) {
    return {
      title: t("integrated.reverseOpenPixCancellationTitle"),
      body: t("integrated.reverseOpenPixCancellationBody", {
        date: formatBillingDate(change.effectiveAt, locale),
      }),
    };
  }

  if (kind === "downgrade_plan" && current?.plan === "premium") {
    return {
      title: t("integrated.keepPremiumConfirmTitle"),
      body: t("integrated.scheduledChangeSupportBodyCancel"),
    };
  }

  return {
    title: t("integrated.scheduledChangeSupportTitleCancel"),
    body: t("integrated.scheduledChangeSupportBodyCancel"),
  };
}
